import partition from 'lodash/partition'

import type { BubbleMounter } from '../types'
import { isDOM } from '@app/shared/predicates'

import buildMounter from '@app/application/communication/handlers/live-input-bubble-mounter'

type MountedBubbles = { container: HTMLElement; mounter: BubbleMounter }

export default (): void => {
  let mounters: MountedBubbles[] = []

  window.onDomLoad(function ($dom) {
    const doc = $dom[0]

    const mounterTarget = getMounterTarget(doc)

    if (!mounterTarget) {
      return
    }

    // A re-render initializes the live-inputs container, which sits inside the
    // container the first mount scanned. Both cover the same inputs, so the
    // older mounter has to unmount its apps: left registered, a note on a
    // deleted fori row keeps its letter and its link to an input that is gone.
    mounters = teardownOverlapping(mounters, mounterTarget)

    const mounter = buildMounter(isPreviewMode(doc))
    mounters = [...mounters, { container: mounterTarget, mounter }]

    mounter.mount({ container: mounterTarget })

    if (isDOM(doc)) {
      doc.addEventListener(
        'turbolinks:visit',
        function () {
          mounters.forEach(({ mounter: tracked }) => tracked.teardown())
          mounters = []
        },
        { once: true }
      )
    }
  })
}

const teardownOverlapping = (
  mounters: MountedBubbles[],
  target: HTMLElement
): MountedBubbles[] => {
  const [overlapping, remaining] = partition(
    mounters,
    ({ container }) =>
      !container.isConnected ||
      container === target ||
      container.contains(target) ||
      target.contains(container)
  )

  overlapping.forEach(({ mounter }) => mounter.teardown())

  return remaining
}

const getMounterTarget = (target: Document | Element): HTMLElement | null => {
  if (!target) {
    return null
  }

  if (isDOM(target)) {
    const shouldAttach = target.querySelector(
      "[data-link-inputs-to-remarks='true']"
    )

    // limit bubbles lookup/insertion only in the current reconciliation, not the comparative reconciliation
    const bubblesContainer = target.querySelector(
      '#reconciliation-table-bloc'
    ) as HTMLElement

    return shouldAttach ? bubblesContainer : null
  } else {
    const effectiveTarget = target as HTMLElement

    const shouldAttach = effectiveTarget.dataset.linkInputsToRemarks === 'true'

    return shouldAttach ? effectiveTarget : null
  }
}

const isPreviewMode = (target: Document | Element): boolean => {
  if (!target) {
    return false
  }

  if (isDOM(target)) {
    return !!target.querySelector(
      "[data-link-inputs-to-remarks='true'][data-inputs-to-remarks='preview']"
    )
  } else {
    const effectiveTarget = target as HTMLElement

    return effectiveTarget.dataset.inputsToRemarks === 'preview'
  }
}
